import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { cn } from "../../../common/u1/cn";
import { DragHandleIcon } from "../../../../assets/ui/icons";
import { EditFooterBar } from "./EditFooterBar";
import { EditSectionLayout } from "./EditSectionLayout";
import { IndustryCallout } from "./EditNotes";
import { SectionStatusPill } from "./SectionStatusPill";
import { useIndustrySection } from "./useIndustrySection";

/* Section 02 — Industries. Reorder only. That is the live behaviour: Industry.jsx offers no add and no remove, because the list comes from the verified trade... */

const SortableRow = ({ row, position, total }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: row.id });

  const primary = position === 1;

  return (
    <li
      ref={setNodeRef}
      /* transform/transition are computed per frame by dnd-kit, so they cannot
         be Tailwind classes. */
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex items-center gap-3 rounded-[10px] border bg-white px-3 py-2.5",
        "cursor-grab touch-none select-none",
        isDragging
          ? "border-[var(--coral)] shadow-[shadow:var(--shadow-lg)]"
          : "border-[var(--line)] hover:border-[var(--line-2)]"
      )}
      {...attributes}
      {...listeners}
    >
      <span className="shrink-0 text-[var(--muted-2)]">
        <DragHandleIcon size={16} />
      </span>

      <span
        className={cn(
          "flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md",
          "text-[11.5px] font-bold font-sans",
          primary
            ? "bg-[var(--coral)] text-white"
            : "bg-[var(--slate-2)] text-[var(--muted)]"
        )}
      >
        {position}
      </span>

      <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-[var(--ink)] font-sans">
        {row.sub_industry}
      </span>

      {primary && (
        <span
          className={cn(
            "shrink-0 rounded-full border border-[var(--orange-border)] bg-[var(--orange-soft)]",
            "px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.06em]",
            "text-[var(--coral-deep)] font-sans"
          )}
        >
          Primary
        </span>
      )}

      {/* The visual order is the saved order, so it has to be readable rather
          than only visible. */}
      <span className="sr-only">
        {`Position ${position} of ${total}`}
      </span>
    </li>
  );
};

export const IndustrySection = ({ onNext, onPrev, onSkip, header, className = "" }) => {
  const { rows, handleDragEnd, save, isSaving, displayStatus } =
    useIndustrySection({ onSaved: onNext });

  const sensors = useSensors(useSensor(PointerSensor));

  return (
    <EditSectionLayout
      className={className}
      header={header}
      footer={
        <EditFooterBar
          onBack={onPrev}
          onSkip={onSkip}
          onContinue={save}
          isLoading={isSaving}
        />
      }
    >
      <div className="flex items-center gap-2.5">
        <h2 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">
          Industries
        </h2>
        <SectionStatusPill status={displayStatus} />
      </div>

      <IndustryCallout className="mt-3.5" />

      <div className="mt-4 rounded-[14px] border border-[var(--line)] bg-white px-4 py-5 sm:px-6">
        <h3 className="m-0 mb-3 text-[13px] font-bold text-[var(--ink)] font-sans">
          Display order
        </h3>

        {rows.length === 0 ? (
          /* The live component renders an empty <ul> here — a bordered box with
             nothing in it and no explanation. */
          <p className="m-0 text-[13px] text-[var(--muted)] font-sans">
            No industries are on this profile yet. They are set from your
            verified trade license.
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={rows.map((r) => r.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                {rows.map((row, index) => (
                  <SortableRow
                    key={row.id}
                    row={row}
                    position={index + 1}
                    total={rows.length}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>

    </EditSectionLayout>
  );
};

export default IndustrySection;
